import { type PathKey } from '~/apps/ninesegsAnalyzer/routes/paths'
import { type CustomerStrategySegmentRoute } from '~/gql/graphql'
import { type Brand } from '~/hooks/useSurveyQuery'
import { type DataDisplayType, type SegmentDisplayType } from '~/utils'

export const pathKey = 'whoAndWhatAnalysis' satisfies PathKey

// セグマップ(5segs/9segs)と矢印(始点終点セグメント)の組み合わせごとにコメントを
// 独立させるためのキー。セグマップが違えば同じセグメント番号同士でも別の矢印なので、
// segmentDisplayType を含めないと fiveSegs/nineSegs 間でコメントが衝突する。
export const buildCommentKey = ({
  fromSegmentNumber,
  segmentDisplayType,
  toSegmentNumber,
}: {
  fromSegmentNumber: number
  segmentDisplayType: SegmentDisplayType
  toSegmentNumber: number
}): string => `${segmentDisplayType}-${fromSegmentNumber}-${toSegmentNumber}`

// TODO(9SEGS-652): 各マニュアルリンクのURLが確定したら個別に差し替える。
// 現状はリンク先が未確定のため、すべて暫定の共通URLを指している。
const MANUAL_BASE_URL = 'https://docs.9segs.com/'

/**
 * WHO&WHAT 分析結果リストから別タブで開くマニュアルのURL。
 *
 * 設問エリア3箇所の?アイコン（NPIドライバー・重視点・積極判定）、
 * 列ヘッダー2箇所の?アイコン（競合に対する優位性列・推奨WHAT軸列）、
 * 「データ解釈時の注意点」リンクから遷移する。箇所ごとに遷移先が異なるためキーで分けている。
 */
export const MANUAL_URLS = {
  npiDriverQuestionHelp: MANUAL_BASE_URL,
  importanceQuestionHelp: MANUAL_BASE_URL,
  liftSaQuestionHelp: MANUAL_BASE_URL,
  comparisonAdvantageColumnHeader: MANUAL_BASE_URL,
  recommendedWhatAxisColumnHeader: MANUAL_BASE_URL,
  interpretationNotes: MANUAL_BASE_URL,
} as const

// AIコメント生成に送る集計データ（tabulationData）を組み立てる。
// 先頭に「{routeLabel}: {from} → {to}」の遷移ルート行を置く。
// analyzer 側はこの行を分析対象として抜き出したりせず「# 入力データ」ブロックへそのまま渡すだけだが、遷移の向きを持つ列は非表示にできるため冗長に持たせる。
export const buildNpiDriverTabulationData = ({
  fromHeaderLabel,
  routeLabel,
  serializedTable,
  toHeaderLabel,
}: {
  fromHeaderLabel: string
  routeLabel: string
  serializedTable: string
  toHeaderLabel: string
}): string =>
  `${routeLabel}: ${fromHeaderLabel} → ${toHeaderLabel}\n${serializedTable}`

type Strategy = {
  strategyType: 'existing' | 'free' | 'new'
  showArrow: boolean
  segmentRoutes: CustomerStrategySegmentRoute[]
}

export type InputValues = {
  brandId: Brand['id']
  dataDisplayType: DataDisplayType
  strategies: Strategy[]
}
